import { getToken } from 'firebase/messaging';
import {
  ElectronPushTokenErrorCode,
  ElectronPushTokenResult,
  PushRegistrationError,
} from 'shared/types/electron';

import { initPushNotifications } from './helpers';

// raw APNs device token (64 hex chars) — not deliverable via FCM until APNs support lands
const apnsTokenPattern = /^[a-f0-9]{64}$/i;

const normalizeBridgeResult = (result: ElectronPushTokenResult): string => {
  const token =
    typeof result === 'string' ? result : result?.token ?? undefined;
  if (token && !apnsTokenPattern.test(token)) {
    return token;
  }
  const bridgeError =
    result && typeof result === 'object' && result.error
      ? String(result.error)
      : undefined;
  if (bridgeError === ElectronPushTokenErrorCode.PermissionDenied) {
    throw new PushRegistrationError(
      ElectronPushTokenErrorCode.PermissionDenied,
    );
  }
  throw new PushRegistrationError(
    ElectronPushTokenErrorCode.RegistrationFailed,
    bridgeError,
  );
};

const fetchElectronPushToken = async (): Promise<string> => {
  const getBridgeToken =
    window.electron?.getFcmToken ?? window.electron?.getPushToken;
  if (!getBridgeToken) {
    throw new PushRegistrationError(
      ElectronPushTokenErrorCode.RegistrationFailed,
    );
  }
  return normalizeBridgeResult(await getBridgeToken());
};

export const fetchFreshPushToken = async () => {
  // never fall through to web push inside electron — chromium has no push service there
  if (window.electron) {
    return fetchElectronPushToken();
  }

  const serviceWorkerRegistration = await window.navigator.serviceWorker?.ready;

  const messaging = initPushNotifications();

  return getToken(messaging, {
    serviceWorkerRegistration,
    vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
  });
};
